import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Button, Divider, Modal, Space } from 'antd';
import { PageProps } from '@/types/PageProps';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import { AppPageHeader, TabContentWrapper } from '@/components/_common';

import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import InformativeExtraFilters from '@/components/Informative/InformativeExtraFilters';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  Informative,
  ListInformativeQuery,
} from '@/core/domain/notification/Informative/Informative.types';
import {
  useDeleteInformative,
  useInformatives,
} from '@/core/domain/notification/Informative/Informative.hooks';
import { useDebounce } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

function InformativePage({ route }: PageProps) {
  const [queryParams, setQueryParams] = useState<ListInformativeQuery>({
    order: 'DESC',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, fetchNextPage, hasNextPage, isFetching } = useInformatives({
    ...queryDebounce,
  });

  const informatives = flatInfiniteData(data);

  const removeInformativeMutation = useDeleteInformative();

  const intl = useIntl();

  const handleRemoveClick = (id: number) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'informative.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'informative.notifications.remove.confirm.content',
      })}`,
      okText: intl.formatMessage({
        id: 'commons.entity.remove',
      }),
      onOk: async () => {
        await removeInformativeMutation.mutateAsync(id);
      },
      okType: 'danger',
    });
  };

  const column: ColumnsType<Informative> = [
    {
      dataIndex: 'informationDate',
      title: <FormattedMessage id="informative.list.header.date" />,
      render: (text) => moment(text).format('DD/MM/YYYY'),
      ellipsis: true,
      width: 120,
    },
    {
      dataIndex: 'employeeName',
      title: <FormattedMessage id="informative.list.header.creator" />,
      ellipsis: true,
      width: 250,
    },
    {
      dataIndex: 'message',
      title: <FormattedMessage id="informative.list.header.message" />,
      ellipsis: true,
      width: 250,
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="informative.list.header.actions" />,
      render: (_, record) => (
        <Space>
          <EditIcon redirect={`/app/informative/${record.id}`} />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record.id)}
            disabled={!hasPermissions([Authorities.MESSAGE_DELETE])}
          />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];
  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Link to="/app/informative/create">
              <Button
                type="primary"
                block
                disabled={!hasPermissions([Authorities.MESSAGE_CREATE])}
              >
                <FormattedMessage id="informative.list.header.new.informative" />
              </Button>
            </Link>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, message: value }))
          }
          searchValue={queryParams.message}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'DESC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <InformativeExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />
        <InfiniteTable<Informative>
          columns={column}
          dataSource={informatives}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      </TabContentWrapper>
    </>
  );
}

export default InformativePage;
